import React, { Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import "./App.css";
import Header from './components/Header';
import GoogleReviews from './components/GoogleReviews';
import Contact from './components/Contact';
import Footer from './components/Footer';
import VisaAssistance from './components/VisaAssistance';
import FlightsPage from './components/FlightsPage';
import NewHomePage from './components/home/NewHomePage';
import GroupToursPage from './pages/GroupToursPage';
import AllPackages from './pages/AllPackages';
import PackageDetail from './pages/PackageDetail';

// New Destination Template System
import DestinationTemplate from './components/DestinationTemplate';
import PackageDetailPage from './pages/PackageDetailPage';

// Theme Holidays Pages
import PilgrimagePage from './pages/ThemeHolidays/Pilgrimage';
import Honeymoon from './pages/ThemeHolidays/Honeymoon';
import FamilyHolidays from './pages/ThemeHolidays/FamilyHolidays';
import HeritageTours from './pages/ThemeHolidays/HeritageTours';
import LuxuryHolidays from './pages/ThemeHolidays/Luxury';
import AdventureTours from './pages/ThemeHolidays/AdventureTours';

// Travel Services Pages
import CruisePackages from './pages/TravelServices/CruisePackages';
import CarRental from './pages/TravelServices/CarRental';

// International Destination Pages
import DubaiPage from './pages/Destinations/DubaiPage';
import SingaporePage from './pages/Destinations/SingaporePage';
import MaldivesPage from './pages/Destinations/MaldivesPage';
import ThailandPage from './pages/Destinations/ThailandPage';
import BaliPage from './pages/Destinations/BaliPage';
import MalaysiaPage from './pages/Destinations/MalaysiaPage';
import AustraliaPage from './pages/Destinations/AustraliaPage';
import EuropePage from './pages/Destinations/EuropePage';
import JapanPage from './pages/Destinations/JapanPage';
import SouthKoreaPage from './pages/Destinations/SouthKoreaPage';
import EgyptPage from './pages/Destinations/EgyptPage';
import JordanPage from './pages/Destinations/JordanPage';
import AzerbaijanPage from './pages/Destinations/AzerbaijanPage';
import GeorgiaPage from './pages/Destinations/GeorgiaPage';
import AboutPage from './pages/AboutPage';
import NotFoundPage from './pages/NotFoundPage';

// India Tour Pages
import RajasthanPage from './pages/IndiaTours/RajasthanPage';
import KeralaPage from './pages/IndiaTours/KeralaPage';
import GoaPage from './pages/IndiaTours/GoaPage';
import KashmirPage from './pages/IndiaTours/KashmirPage';
import LadakhPage from './pages/IndiaTours/LadakhPage';
import NortheastPage from './pages/IndiaTours/NortheastPage';
import HimachalPage from './pages/IndiaTours/HimachalPage';
import UttarakhandPage from './pages/IndiaTours/UttarakhandPage';
import AndamanPage from './pages/IndiaTours/AndamanPage';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, AdminLogin, ProtectedRoute } from './AdminAuth';
import BhutanPage from './pages/Destinations/BhutanPage';
import NepalPage from './pages/Destinations/NepalPage';
import SriLankaPage from './pages/Destinations/SriLankaPage';
import MauritiusPage from './pages/Destinations/MauritiusPage';
import VietnamPage from './pages/Destinations/VietnamPage';
import RussiaPage from './pages/Destinations/RussiaPage';
import USAPage from './pages/Destinations/USAPage';
import IndiaPage from './pages/Destinations/IndiaPage';
import { PrivacyPolicy, TermsConditions, CancellationPolicy, RefundPolicy, ShippingPolicy, ContactUs } from './pages/Legal/PolicyPages';

import CustomizePackagePopup from './components/CustomizePackagePopup';
import MobileCallBar from './components/site/MobileCallBar';

// Admin screens (loaded on demand)
const AdminDashboard = lazy(() => import('./AdminDashboard'));
const TravelCRM = lazy(() => import('./pages/CRM/TravelCRM'));
const AccountsApp = lazy(() => import('./pages/Accounts/AccountsApp'));
const TripPage = lazy(() => import('./crm/screens/ProposalPublic'));
const Integrations = lazy(() => import('./pages/Admin/Integrations'));
const PackageManager = lazy(() => import('./pages/Admin/PackageManager'));
const PackageEditor = lazy(() => import('./pages/Admin/PackageEditor'));
const HotDealsManagement = lazy(() => import('./HotDealsManagement'));

// Home Page Component
const HomePage = () => (
  <>
    <Header />
    <NewHomePage />
    <GoogleReviews />
    <Contact />
    <CustomizePackagePopup />
    <Footer />
  </>
);

// Destination Page Wrapper - uses the new template system
const DestinationPageWrapper = () => {
  const { destinationId } = require('react-router-dom').useParams();
  return <DestinationTemplate destinationId={destinationId} />;
};

function App() {
  return (
    <Router>
      <AuthProvider>
            <div className="App">
            {/* Admin screens are loaded on demand so they don't weigh down the public site */}
            <Suspense fallback={null}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<HomePage />} />
              {/* Client itinerary link shared by staff (no site header/footer – the page carries its own VK Tours branding) */}
              <Route path="/trip/:shareId" element={<TripPage />} />
              <Route path="/tour-packages" element={<AllPackages />} />
              <Route path="/packages" element={<Navigate to="/tour-packages" replace />} />
              <Route path="/package/:slug" element={<PackageDetail />} />
              <Route path="/flights" element={<FlightsPage />} />
              
              {/* SEO-Friendly International Destination Routes */}
              <Route path="/dubai-tour-packages" element={<DubaiPage />} />
              <Route path="/singapore-tour-packages" element={<SingaporePage />} />
              <Route path="/maldives-honeymoon-packages" element={<MaldivesPage />} />
              <Route path="/thailand-tour-packages" element={<ThailandPage />} />
              <Route path="/bali-holiday-packages" element={<BaliPage />} />
              <Route path="/malaysia-tour-packages" element={<MalaysiaPage />} />
              <Route path="/bhutan-tour-packages" element={<BhutanPage />} />
              <Route path="/nepal-tour-packages" element={<NepalPage />} />
              <Route path="/sri-lanka-tour-packages" element={<SriLankaPage />} />
              <Route path="/mauritius-honeymoon-packages" element={<MauritiusPage />} />
              <Route path="/vietnam-tour-packages" element={<VietnamPage />} />
              <Route path="/russia-tour-packages" element={<RussiaPage />} />
              <Route path="/usa-tour-packages" element={<USAPage />} />
              <Route path="/india-tour-packages" element={<IndiaPage />} />
              <Route path="/australia-tour-packages" element={<AustraliaPage />} />
              <Route path="/europe-tour-packages" element={<EuropePage />} />
              <Route path="/japan-tour-packages" element={<JapanPage />} />
              <Route path="/south-korea-tour-packages" element={<SouthKoreaPage />} />
              <Route path="/egypt-tour-packages" element={<EgyptPage />} />
              <Route path="/jordan-tour-packages" element={<JordanPage />} />
              <Route path="/azerbaijan-tour-packages" element={<AzerbaijanPage />} />
              <Route path="/georgia-tour-packages" element={<GeorgiaPage />} />
              
              {/* Redirects from old URLs to new SEO URLs */}
              <Route path="/destinations/dubai" element={<Navigate to="/dubai-tour-packages" replace />} />
              <Route path="/destinations/singapore" element={<Navigate to="/singapore-tour-packages" replace />} />
              <Route path="/destinations/maldives" element={<Navigate to="/maldives-honeymoon-packages" replace />} />
              <Route path="/destinations/thailand" element={<Navigate to="/thailand-tour-packages" replace />} />
              <Route path="/destinations/bali" element={<Navigate to="/bali-holiday-packages" replace />} />
              <Route path="/destinations/malaysia" element={<Navigate to="/malaysia-tour-packages" replace />} />
              <Route path="/destinations/bhutan" element={<Navigate to="/bhutan-tour-packages" replace />} />
              <Route path="/destinations/nepal" element={<Navigate to="/nepal-tour-packages" replace />} />
              <Route path="/destinations/sri-lanka" element={<Navigate to="/sri-lanka-tour-packages" replace />} />
              <Route path="/destinations/mauritius" element={<Navigate to="/mauritius-honeymoon-packages" replace />} />
              <Route path="/destinations/vietnam" element={<Navigate to="/vietnam-tour-packages" replace />} />
              <Route path="/destinations/russia" element={<Navigate to="/russia-tour-packages" replace />} />
              <Route path="/destinations/usa" element={<Navigate to="/usa-tour-packages" replace />} />
              <Route path="/destinations/india" element={<Navigate to="/india-tour-packages" replace />} />
              <Route path="/destinations/australia" element={<Navigate to="/australia-tour-packages" replace />} />
              <Route path="/destinations/europe" element={<Navigate to="/europe-tour-packages" replace />} />
              <Route path="/destinations/japan" element={<Navigate to="/japan-tour-packages" replace />} />
              <Route path="/destinations/south-korea" element={<Navigate to="/south-korea-tour-packages" replace />} />
              <Route path="/destinations/egypt" element={<Navigate to="/egypt-tour-packages" replace />} />
              <Route path="/destinations/jordan" element={<Navigate to="/jordan-tour-packages" replace />} />
              <Route path="/destinations/azerbaijan" element={<Navigate to="/azerbaijan-tour-packages" replace />} />
              <Route path="/destinations/georgia" element={<Navigate to="/georgia-tour-packages" replace />} />
              
              {/* SEO-Friendly Indian Domestic Destinations */}
              <Route path="/rajasthan-tour-packages" element={<RajasthanPage />} />
              <Route path="/kerala-tour-packages" element={<KeralaPage />} />
              <Route path="/goa-holiday-packages" element={<GoaPage />} />
              <Route path="/kashmir-tour-packages" element={<KashmirPage />} />
              <Route path="/ladakh-bike-tour-packages" element={<LadakhPage />} />
              <Route path="/northeast-india-tour-packages" element={<NortheastPage />} />
              <Route path="/himachal-tour-packages" element={<HimachalPage />} />
              <Route path="/uttarakhand-tour-packages" element={<UttarakhandPage />} />
              <Route path="/andaman-tour-packages" element={<AndamanPage />} />
              <Route path="/andhra-telangana-tour-packages" element={<DestinationTemplate destinationId="andhra" />} />
              <Route path="/karnataka-tour-packages" element={<DestinationTemplate destinationId="karnataka" />} />
              <Route path="/tamil-nadu-tour-packages" element={<DestinationTemplate destinationId="tamilnadu" />} />
              <Route path="/uttar-pradesh-tour-packages" element={<DestinationTemplate destinationId="uttarpradesh" />} />
              <Route path="/madhya-pradesh-tour-packages" element={<DestinationTemplate destinationId="madhyapradesh" />} />
              <Route path="/maharashtra-tour-packages" element={<DestinationTemplate destinationId="maharashtra" />} />
              <Route path="/odisha-tour-packages" element={<DestinationTemplate destinationId="odisha" />} />
              <Route path="/gujarat-tour-packages" element={<DestinationTemplate destinationId="gujarat" />} />
              <Route path="/lakshadweep-tour-packages" element={<DestinationTemplate destinationId="lakshadweep" />} />
              
              {/* New Destination Template Routes (Google Sheets Powered) */}
              <Route path="/destination/:destinationId" element={<DestinationPageWrapper />} />
              <Route path="/destination/:destinationId/package/:packageId" element={<PackageDetailPage />} />
              
              {/* Redirects from old India tour URLs */}
              <Route path="/india-tours/rajasthan" element={<Navigate to="/rajasthan-tour-packages" replace />} />
              <Route path="/india-tours/kerala" element={<Navigate to="/kerala-tour-packages" replace />} />
              <Route path="/india-tours/goa" element={<Navigate to="/goa-holiday-packages" replace />} />
              <Route path="/india-tours/kashmir" element={<Navigate to="/kashmir-tour-packages" replace />} />
              <Route path="/india-tours/ladakh" element={<Navigate to="/ladakh-bike-tour-packages" replace />} />
              <Route path="/india-tours/northeast" element={<Navigate to="/northeast-india-tour-packages" replace />} />
              <Route path="/india-tours/himachal" element={<Navigate to="/himachal-tour-packages" replace />} />
              <Route path="/india-tours/uttarakhand" element={<Navigate to="/uttarakhand-tour-packages" replace />} />
              <Route path="/india-tours/andaman" element={<Navigate to="/andaman-tour-packages" replace />} />
              
              {/* SEO-Friendly Theme Holidays Routes */}
              <Route path="/pilgrimage-tour-packages" element={<PilgrimagePage />} />
              <Route path="/honeymoon-packages" element={<Honeymoon />} />
              <Route path="/family-holiday-packages" element={<FamilyHolidays />} />
              <Route path="/heritage-tour-packages" element={<HeritageTours />} />
              <Route path="/luxury-holiday-packages" element={<LuxuryHolidays />} />
              <Route path="/luxury-tour-packages" element={<Navigate to="/luxury-holiday-packages" replace />} />
              <Route path="/adventure-tour-packages" element={<AdventureTours />} />
              
              {/* Redirects from old theme URLs */}
              <Route path="/theme-holidays/pilgrimage" element={<Navigate to="/pilgrimage-tour-packages" replace />} />
              <Route path="/theme-holidays/honeymoon" element={<Navigate to="/honeymoon-packages" replace />} />
              <Route path="/theme-holidays/family" element={<Navigate to="/family-holiday-packages" replace />} />
              <Route path="/theme-holidays/heritage" element={<Navigate to="/heritage-tour-packages" replace />} />
              <Route path="/theme-holidays/adventure" element={<Navigate to="/adventure-tour-packages" replace />} />
              
              {/* Travel Services Routes */}
              <Route path="/travel-services/visa-assistance" element={<VisaAssistance />} />
              <Route path="/travel-services/cruise" element={<CruisePackages />} />
              <Route path="/travel-services/car-rental" element={<CarRental />} />
              
              {/* Group Tours Route */}
              <Route path="/group-tours" element={<GroupToursPage />} />
              
              {/* About Page */}
              <Route path="/about" element={<AboutPage />} />
              
              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route 
                path="/admin/packages" 
                element={
                  <ProtectedRoute>
                    <PackageManager />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/packages/new" 
                element={
                  <ProtectedRoute>
                    <PackageEditor />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/packages/edit/:packageId" 
                element={
                  <ProtectedRoute>
                    <PackageEditor />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/dashboard" 
                element={
                  <ProtectedRoute>
                    <AdminDashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/integrations" 
                element={
                  <ProtectedRoute>
                    <Integrations />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/crm" 
                element={
                  <ProtectedRoute>
                    <TravelCRM />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/accounts" 
                element={
                  <ProtectedRoute>
                    <AccountsApp />
                  </ProtectedRoute>
                } 
              />
              

              {/* Hot Deals Management */}
              <Route 
                path="/admin/hot-deals" 
                element={
                  <ProtectedRoute>
                    <HotDealsManagement />
                  </ProtectedRoute>
                } 
              />
              
              {/* Redirect /admin to dashboard */}
              <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />

              {/* Legal / Policy pages (public, linked from footer & payment gateways) */}
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-conditions" element={<TermsConditions />} />
              <Route path="/terms" element={<Navigate to="/terms-conditions" replace />} />
              <Route path="/cancellation-policy" element={<CancellationPolicy />} />
              <Route path="/refund-policy" element={<RefundPolicy />} />
              <Route path="/shipping-policy" element={<ShippingPolicy />} />
              <Route path="/service-delivery" element={<Navigate to="/shipping-policy" replace />} />
              <Route path="/contact-us" element={<ContactUs />} />
              <Route path="/contact" element={<Navigate to="/contact-us" replace />} />

              {/* Anything else: a proper "page not found" page instead of a blank screen */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            </Suspense>
            <Toaster />
            <MobileCallBar />
            </div>
      </AuthProvider>
    </Router>
  );
}

export default App;
